import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersService, User, UserPermission } from '../services/users.service';
import { dlpService, DlpBuyLicense, DlpExceptionRequest, AdminIssueSite, AdminCommonBlockedSite, AdminLicenseStat, AdminExEmployee } from '../services/dlp.service';
import { nacService } from '../services/nac.service';
import { companyService, CompanyInfo } from '../services/company.service';
import { reportsService } from '../services/reports.service';
import { resultsService } from '../services/results.service';
import { useAuth } from '../contexts/AuthContext';
import './AdminPage.css';

type AdminCategory = 'USER' | 'APT' | 'DLP' | 'NAC';
type DlpTab = 'dlp-report' | 'dlp-license' | 'dlp-exception';
type NacTab = 'nac-report' | 'nac-license';

const AdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = (searchParams.get('category') as AdminCategory) || 'USER';
  const tab = searchParams.get('tab') || '';
  
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  useEffect(() => {
    if (category === 'USER') {
      loadUsers();
    }
    // URL 파라미터가 없으면 기본값 설정
    if (!searchParams.get('category')) {
      setSearchParams({ category: 'USER' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      setMessage({ type: 'error', text: '사용자 목록을 불러오는데 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: 'admin' | 'editor' | 'reviewer' | 'viewer') => {
    try {
      await usersService.updateRole(userId, { role: newRole });
      setMessage({ type: 'success', text: '권한이 변경되었습니다.' });
      loadUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '권한 변경에 실패했습니다.',
      });
    }
  };

  const handleDelete = async (userId: number, userEmail: string) => {
    if (!window.confirm(`정말로 ${userEmail} 사용자를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await usersService.delete(userId);
      setMessage({ type: 'success', text: '사용자가 삭제되었습니다.' });
      loadUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '사용자 삭제에 실패했습니다.',
      });
    }
  };

  const handleResetPassword = async (userId: number, userEmail: string) => {
    if (!window.confirm(`${userEmail} 사용자의 비밀번호를 초기화하시겠습니까?`)) {
      return;
    }

    try {
      const result = await usersService.resetPassword(userId);
      setMessage({
        type: 'success',
        text: result.tempPassword
          ? `비밀번호가 초기화되었습니다. 임시 비밀번호: ${result.tempPassword}`
          : result.message,
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '비밀번호 초기화에 실패했습니다.',
      });
    }
  };

  const handleTabChange = (newTab: string) => {
    setSearchParams({ category, tab: newTab });
  };

  // 카테고리별 기본 탭 결정
  const getActiveTab = (): string => {
    if (category === 'USER' || category === 'APT') {
      return '';
    }
    if (category === 'DLP') {
      return tab || 'dlp-report';
    }
    if (category === 'NAC') {
      return tab || 'nac-report';
    }
    return '';
  };

  const activeTab = getActiveTab();

  return (
    <div className="admin-page">
      <h1>관리자 페이지</h1>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="admin-content-area">
          {/* 탭 메뉴 (DLP, NAC만 표시) */}
          {(category === 'DLP' || category === 'NAC') && (
            <div className="admin-tabs">
              {category === 'DLP' && (
                <>
                  <button
                    className={`admin-tab ${activeTab === 'dlp-report' ? 'active' : ''}`}
                    onClick={() => handleTabChange('dlp-report')}
                  >
                    DLP 운영보고서 관리
                  </button>
                  <button
                    className={`admin-tab ${activeTab === 'dlp-license' ? 'active' : ''}`}
                    onClick={() => handleTabChange('dlp-license')}
                  >
                    DLP 라이센스 관리
                  </button>
                  <button
                    className={`admin-tab ${activeTab === 'dlp-exception' ? 'active' : ''}`}
                    onClick={() => handleTabChange('dlp-exception')}
                  >
                    DLP 예외처리 관리
                  </button>
                </>
              )}
              {category === 'NAC' && (
                <>
                  <button
                    className={`admin-tab ${activeTab === 'nac-report' ? 'active' : ''}`}
                    onClick={() => handleTabChange('nac-report')}
                  >
                    NAC 운영보고서 관리
                  </button>
                  <button
                    className={`admin-tab ${activeTab === 'nac-license' ? 'active' : ''}`}
                    onClick={() => handleTabChange('nac-license')}
                  >
                    NAC 라이센스 관리
                  </button>
                </>
              )}
            </div>
          )}

          {/* 컨텐츠 */}
          <div className="admin-tab-content">
            {category === 'USER' && (
              <UserManagementSection
                users={users}
                isLoading={isLoading}
                onRoleChange={handleRoleChange}
                onDelete={handleDelete}
                onResetPassword={handleResetPassword}
              />
            )}
            {category === 'APT' && <AptSpamReportSection />}
            {category === 'DLP' && activeTab === 'dlp-report' && <DlpReportManagementSection />}
            {category === 'DLP' && activeTab === 'dlp-license' && <DlpLicenseManagementSection />}
            {category === 'DLP' && activeTab === 'dlp-exception' && <DlpExceptionManagementSection />}
            {category === 'NAC' && activeTab === 'nac-report' && <NacReportManagementSection />}
            {category === 'NAC' && activeTab === 'nac-license' && <NacLicenseManagementSection />}
          </div>
      </div>
    </div>
  );
};

// 유저관리 섹션
const UserManagementSection: React.FC<{
  users: User[];
  isLoading: boolean;
  onRoleChange: (userId: number, role: 'admin' | 'editor' | 'reviewer' | 'viewer') => void;
  onDelete: (userId: number, userEmail: string) => void;
  onResetPassword: (userId: number, userEmail: string) => void;
}> = ({ users, isLoading, onRoleChange, onDelete, onResetPassword }) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  return (
    <div>
      <h2>유저관리</h2>
      {isLoading ? (
        <div className="loading">사용자 목록을 불러오는 중...</div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>이름</th>
                <th>회사</th>
                <th>부서</th>
                <th>이메일</th>
                <th>권한</th>
                <th>가입일</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-data">
                    등록된 사용자가 없습니다.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.name}</td>
                    <td>{user.company}</td>
                    <td>{user.department}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>{user.role}</span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString('ko-KR')}</td>
                    <td>
                      <div className="action-buttons">
                        <select
                          value={user.role}
                          onChange={(e) =>
                            onRoleChange(
                              user.id,
                              e.target.value as 'admin' | 'editor' | 'reviewer' | 'viewer'
                            )
                          }
                          className="role-select"
                        >
                          <option value="viewer">viewer</option>
                          <option value="reviewer">reviewer</option>
                          <option value="editor">editor</option>
                          <option value="admin">admin</option>
                        </select>
                        <button
                          className="btn-secondary"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowPermissionModal(true);
                          }}
                        >
                          권한
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => onDelete(user.id, user.email)}
                        >
                          삭제
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => onResetPassword(user.id, user.email)}
                        >
                          비밀번호 초기화
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showPermissionModal && selectedUser && (
        <PermissionModal
          user={selectedUser}
          onClose={() => {
            setShowPermissionModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

// 권한 설정 모달
const PermissionModal: React.FC<{
  user: User;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const [, setPermissions] = useState<UserPermission[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [selectedPageType, setSelectedPageType] = useState<'APT' | 'DLP' | 'NAC'>('APT');
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, selectedPageType]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // 사용자 권한 가져오기
      const userPerms = await usersService.getUserPermissions(user.id);
      const filteredPerms = userPerms.filter((p) => p.pageType === selectedPageType);
      setPermissions(filteredPerms);
      
      // 선택된 회사 초기화
      const selected = new Set(filteredPerms.map((p) => p.companyName));
      setSelectedCompanies(selected);

      // pageType별 회사 목록 가져오기
      const companies = await companyService.getByPageType(selectedPageType);
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyToggle = (companyName: string) => {
    const newSelected = new Set(selectedCompanies);
    if (newSelected.has(companyName)) {
      newSelected.delete(companyName);
    } else {
      newSelected.add(companyName);
    }
    setSelectedCompanies(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCompanies.size === filteredCompanies.length) {
      setSelectedCompanies(new Set());
    } else {
      setSelectedCompanies(new Set(filteredCompanies.map((c) => c.companyName)));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // 선택된 권한들 수집
      const selectedPermissions: Array<{ companyName: string; pageType: 'APT' | 'DLP' | 'NAC' }> = 
        Array.from(selectedCompanies).map((companyName) => ({
          companyName,
          pageType: selectedPageType,
        }));

      await usersService.setUserPermissions(user.id, selectedPermissions);
      alert('권한이 저장되었습니다.');
      onClose();
    } catch (error: any) {
      alert('권한 저장에 실패했습니다: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  // 검색 필터링
  const filteredCompanies = availableCompanies.filter((company) =>
    company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>권한 설정 - {user.email}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="page-type-selector">
            <label>Page Type:</label>
            <select
              value={selectedPageType}
              onChange={(e) => setSelectedPageType(e.target.value as 'APT' | 'DLP' | 'NAC')}
            >
              <option value="APT">APT</option>
              <option value="DLP">DLP</option>
              <option value="NAC">NAC</option>
            </select>
          </div>
          {isLoading ? (
            <div className="loading">로딩 중...</div>
          ) : (
            <>
              <div className="permission-search">
                <input
                  type="text"
                  placeholder="회사명 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button className="btn-select-all" onClick={handleSelectAll}>
                  {selectedCompanies.size === filteredCompanies.length ? '전체 해제' : '전체 선택'}
                </button>
              </div>
              <div className="permission-list">
                {filteredCompanies.length === 0 ? (
                  <p className="no-companies">해당 Page Type에 사용 가능한 회사가 없습니다.</p>
                ) : (
                  <div className="company-checkbox-list">
                    {filteredCompanies.map((company) => (
                      <label key={company.orgCode} className="company-checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedCompanies.has(company.companyName)}
                          onChange={() => handleCompanyToggle(company.companyName)}
                        />
                        <span>{company.companyName}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="selected-count">
                  선택된 회사: {selectedCompanies.size}개 / {filteredCompanies.length}개
                </div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            취소
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

// DLP 운영보고서 관리 섹션
const DlpReportManagementSection: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [summaryContent, setSummaryContent] = useState<string>('');
  // 고정된 링크 목록 (base-rule.mdc에 정의된 link_id)
  const fixedLinks = [
    { linkId: 'dlp_ops_data', linkName: 'DLP 운영데이터' },
    { linkId: 'dlp_attach_except', linkName: '예외정책 누적현황' },
    { linkId: 'dlp_attach_usb', linkName: '보안 USB 등록현황' },
    { linkId: 'dlp_attach_file', linkName: '파일첨부 건수현황' },
    { linkId: 'dlp_attach_watermark', linkName: '워터마크 해제 신청 현황' },
    { linkId: 'dlp_attach_sblock', linkName: '소프트웨어 차단현황' },
    { linkId: 'dlp_attach_wblock', linkName: '웹사이트 차단현황' },
  ];

  const [attachments, setAttachments] = useState<Record<string, { linkId: string; linkName: string; linkUrl: string; id?: number; saved?: boolean }>>({});
  const [savingLinks, setSavingLinks] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const { user } = useAuth();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedCompany]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const periods = await dlpService.getAvailablePeriods();
      setAvailablePeriods(periods);
      if (periods.length > 0) {
        setSelectedPeriod(periods[0]);
      }

      const companies = await companyService.getByPageType('DLP');
      setAvailableCompanies(companies);
      if (companies.length === 1) {
        setSelectedCompany(companies[0].companyName);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportData = async () => {
    if (!selectedPeriod || !selectedCompany) return;

    setIsLoading(true);
    try {
      // 요약 내용 로드
      const summary = await dlpService.getSummary(selectedPeriod, selectedCompany);
      setSummaryContent(summary?.summaryContent || '');

      // 링크 목록 로드
      const links = await dlpService.getAttachments(selectedPeriod, selectedCompany);
      const linksMap: Record<string, { linkId: string; linkName: string; linkUrl: string; id?: number; saved?: boolean }> = {};
      
      // 고정된 링크 목록을 기반으로 맵 생성
      fixedLinks.forEach((fixedLink) => {
        const existingLink = links.find((l) => l.linkId === fixedLink.linkId);
        linksMap[fixedLink.linkId] = {
          linkId: fixedLink.linkId,
          linkName: fixedLink.linkName,
          linkUrl: existingLink?.linkUrl || '',
          id: existingLink?.id,
          saved: !!existingLink, // 기존 링크가 있으면 저장됨
        };
      });
      
      setAttachments(linksMap);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSave = async () => {
    if (!selectedPeriod || !selectedCompany) {
      setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
      return;
    }

    // reviewer인 경우 저장하지 않고 팝업 표시
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    setIsSaving(true);
    try {
      // 요약 저장
      await dlpService.saveSummary(selectedPeriod, selectedCompany, summaryContent);

      // 저장되지 않은 모든 링크 저장
      await saveAllUnsavedLinks();

      setMessage({ type: 'success', text: '저장되었습니다.' });
      // 데이터 다시 로드
      loadReportData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedPeriod || !selectedCompany) {
      setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
      return;
    }

    // reviewer인 경우 저장하지 않고 팝업 표시
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    if (!window.confirm('이 보고서를 발행하시겠습니까?')) {
      return;
    }

    setIsSaving(true);
    try {
      // 요약 저장
      await dlpService.saveSummary(selectedPeriod, selectedCompany, summaryContent);

      // 저장되지 않은 모든 링크 저장
      await saveAllUnsavedLinks();

      // 퍼블리시
      await reportsService.publish(selectedPeriod, selectedCompany, 'DLP');
      setMessage({ type: 'success', text: '발행되었습니다.' });
      // 데이터 다시 로드
      loadReportData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '발행에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 개별 링크 저장 (upsert)
  const handleSaveLink = async (linkId: string) => {
    if (!selectedPeriod || !selectedCompany) {
      setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
      return;
    }

    // reviewer인 경우 저장하지 않고 팝업 표시
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    const attachment = attachments[linkId];
    if (!attachment || !attachment.linkUrl) {
      setMessage({ type: 'error', text: '링크 URL을 입력해주세요.' });
      return;
    }

    setSavingLinks({ ...savingLinks, [linkId]: true });
    try {
      if (attachment.id) {
        // 업데이트
        await dlpService.updateAttachment(attachment.id, {
          linkName: attachment.linkName,
          linkUrl: attachment.linkUrl,
          updateDate: new Date(),
        });
        // 업데이트 후 saved 상태 설정
        setAttachments({
          ...attachments,
          [linkId]: { ...attachment, saved: true },
        });
      } else {
        // 생성
        const created = await dlpService.createAttachment({
          period: selectedPeriod,
          companyName: selectedCompany,
          linkId: attachment.linkId,
          linkName: attachment.linkName,
          linkUrl: attachment.linkUrl,
          updateDate: new Date(),
        });
        // 생성된 ID 저장
        setAttachments({
          ...attachments,
          [linkId]: { ...attachment, id: created.id, saved: true },
        });
      }
      setMessage({ type: 'success', text: '링크가 저장되었습니다.' });
      // 데이터 다시 로드하지 않고 상태만 업데이트
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '링크 저장에 실패했습니다.',
      });
    } finally {
      setSavingLinks({ ...savingLinks, [linkId]: false });
    }
  };

  const handleLinkUrlChange = (linkId: string, linkUrl: string) => {
    setAttachments({
      ...attachments,
      [linkId]: { ...attachments[linkId], linkUrl, saved: false }, // URL 변경 시 saved 상태 해제
    });
  };

  // 저장되지 않은 모든 링크 저장
  const saveAllUnsavedLinks = async () => {
    if (!selectedPeriod || !selectedCompany) {
      return;
    }

    const unsavedLinks = Object.values(attachments).filter(
      (attachment) => attachment.linkUrl && !attachment.saved
    );

    if (unsavedLinks.length === 0) {
      return; // 저장할 링크가 없음
    }

    for (const attachment of unsavedLinks) {
      const linkId = attachment.linkId;
      setSavingLinks({ ...savingLinks, [linkId]: true });
      try {
        if (attachment.id) {
          // 업데이트
          await dlpService.updateAttachment(attachment.id, {
            linkName: attachment.linkName,
            linkUrl: attachment.linkUrl,
            updateDate: new Date(),
          });
        } else {
          // 생성
          const created = await dlpService.createAttachment({
            period: selectedPeriod,
            companyName: selectedCompany,
            linkId: attachment.linkId,
            linkName: attachment.linkName,
            linkUrl: attachment.linkUrl,
            updateDate: new Date(),
          });
          // 생성된 ID 저장
          setAttachments({
            ...attachments,
            [linkId]: { ...attachment, id: created.id, saved: true },
          });
          continue; // 다음 링크로
        }
        // 업데이트 후 saved 상태 설정
        setAttachments({
          ...attachments,
          [linkId]: { ...attachment, saved: true },
        });
      } catch (error: any) {
        console.error(`링크 저장 실패 (${linkId}):`, error);
      } finally {
        setSavingLinks({ ...savingLinks, [linkId]: false });
      }
    }
  };

  return (
    <div>
      <h2>DLP 운영보고서 관리</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="report-management-controls">
        <div className="control-group">
          <label>기간:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availablePeriods.map((period) => (
              <option key={period} value={period}>
                {period.slice(0, 4)}년 {period.slice(4)}월
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>회사:</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availableCompanies.map((company) => (
              <option key={company.orgCode} value={company.companyName}>
                {company.companyName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedPeriod && selectedCompany && (
        <>
          <div className="summary-section" style={{ marginBottom: '3rem' }}>
            <h3>요약 작성</h3>
            <textarea
              className="summary-editor"
              value={summaryContent}
              onChange={(e) => setSummaryContent(e.target.value)}
              placeholder="Markdown 형식으로 요약을 작성하세요..."
              rows={10}
            />
            <button
              className="btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={async () => {
                if (!selectedPeriod || !selectedCompany) {
                  setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
                  return;
                }
                if (user?.role === 'reviewer') {
                  alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
                  return;
                }
                setIsSaving(true);
                try {
                  await dlpService.saveSummary(selectedPeriod, selectedCompany, summaryContent);
                  setMessage({ type: 'success', text: '요약이 저장되었습니다.' });
                  loadReportData();
                } catch (error: any) {
                  setMessage({
                    type: 'error',
                    text: error.response?.data?.message || '저장에 실패했습니다.',
                  });
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>

          <div className="links-section" style={{ marginBottom: '3rem' }}>
            <h3>링크 입력</h3>
            <table className="links-table">
              <thead>
                <tr>
                  <th>링크명</th>
                  <th>링크 URL</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {fixedLinks.map((fixedLink) => {
                  const attachment = attachments[fixedLink.linkId] || {
                    linkId: fixedLink.linkId,
                    linkName: fixedLink.linkName,
                    linkUrl: '',
                  };
                  const isSaving = savingLinks[fixedLink.linkId] || false;
                  
                  return (
                    <tr key={fixedLink.linkId}>
                      <td>{fixedLink.linkName}</td>
                      <td>
                        <input
                          type="text"
                          placeholder="링크 URL을 입력하세요"
                          value={attachment.linkUrl}
                          onChange={(e) => handleLinkUrlChange(fixedLink.linkId, e.target.value)}
                          className="link-url-input"
                          disabled={isSaving}
                        />
                      </td>
                      <td>
                        {attachment.saved ? (
                          <span className="link-saved-icon">✓</span>
                        ) : (
                          <button
                            className="btn-primary"
                            onClick={() => handleSaveLink(fixedLink.linkId)}
                            disabled={isSaving || savingLinks[fixedLink.linkId] || !selectedPeriod || !selectedCompany || !attachment.linkUrl}
                          >
                            {savingLinks[fixedLink.linkId] ? '저장 중...' : '저장'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* PUBLISH 버튼 */}
          <div style={{ marginTop: '3rem', display: 'flex', justifyContent: 'flex-end', padding: '2rem 0', borderTop: '2px solid #ddd' }}>
            <button
              className="btn-primary"
              style={{ 
                padding: '0.75rem 2rem', 
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: '#28a745',
                borderColor: '#28a745',
              }}
              onClick={handlePublish}
              disabled={isSaving}
            >
              {isSaving ? '발행 중...' : 'PUBLISH'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// DLP 라이센스 관리 섹션
const DlpLicenseManagementSection: React.FC = () => {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState<DlpBuyLicense[]>([]);
  const [editedLicenses, setEditedLicenses] = useState<Record<string, string>>({});
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // 엑셀 업로드
  const [uploadStatus, setUploadStatus] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<{ type: 'success' | 'error'; message: string; errors?: string[] } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    loadData();
    loadUploadStatus();
  }, []);

  const loadUploadStatus = async () => {
    try {
      const status = await dlpService.getLatestUploadStatus();
      setUploadStatus(status);
    } catch (error) {
      console.error('Failed to load upload status:', error);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [licensesData, companiesData] = await Promise.all([
        dlpService.getAllLicenses(),
        companyService.getByPageType('DLP'),
      ]);
      setLicenses(licensesData);
      setAvailableCompanies(companiesData);
      
      // 초기 편집 상태 설정 (구매수량만)
      const initialEdited: Record<string, string> = {};
      licensesData.forEach((license) => {
        initialEdited[license.companyName] = license.amount.toString();
      });
      // availableCompanies에 있지만 licenses에 없는 회사는 빈 문자열로 초기화
      companiesData.forEach((company) => {
        if (!initialEdited[company.companyName]) {
          initialEdited[company.companyName] = '';
        }
      });
      setEditedLicenses(initialEdited);
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'error', text: '데이터를 불러오는데 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (companyName: string, value: string) => {
    // 숫자와 쉼표만 허용
    const cleanedValue = value.replace(/[^0-9,]/g, '');
    setEditedLicenses((prev) => ({
      ...prev,
      [companyName]: cleanedValue,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    
    try {
      // 모든 회사에 대해 업데이트된 수량이 있으면 저장
      const savePromises: Promise<any>[] = [];
      
      for (const license of licenses) {
        const editedAmount = editedLicenses[license.companyName];
        if (editedAmount) {
          const amountNum = parseInt(editedAmount.replace(/,/g, ''), 10);
          if (!isNaN(amountNum) && amountNum !== license.amount) {
            // createLicense를 호출하면 before/after가 자동으로 계산됨
            const company = availableCompanies.find((c) => c.companyName === license.companyName);
            if (company) {
              savePromises.push(
                dlpService.createLicense({
                  orgCode: company.orgCode,
                  companyName: license.companyName,
                  amount: amountNum,
                  updateDate: new Date(),
                  updateUser: user?.email || 'system',
                })
              );
            }
          }
        }
      }

      // 새로 추가된 회사들 (licenses에 없지만 editedLicenses에 있는 경우)
      for (const company of availableCompanies) {
        if (!licenses.find((l) => l.companyName === company.companyName)) {
          const editedAmount = editedLicenses[company.companyName];
          if (editedAmount) {
            const amountNum = parseInt(editedAmount.replace(/,/g, ''), 10);
            if (!isNaN(amountNum) && amountNum > 0) {
              savePromises.push(
                dlpService.createLicense({
                  orgCode: company.orgCode,
                  companyName: company.companyName,
                  amount: amountNum,
                  updateDate: new Date(),
                  updateUser: user?.email || 'system',
                })
              );
            }
          }
        }
      }

      if (savePromises.length === 0) {
        setMessage({ type: 'error', text: '변경된 내용이 없습니다.' });
        setIsSaving(false);
        return;
      }

      await Promise.all(savePromises);
      await loadData();
      setMessage({ type: 'success', text: '라이센스 정보가 저장되었습니다.' });
    } catch (error: any) {
      console.error('Failed to save licenses:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '라이센스 저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 모든 회사 목록 (licenses에 있는 회사 + availableCompanies에 있는 회사)
  const allCompanies = React.useMemo(() => {
    const companyMap = new Map<string, CompanyInfo>();
    
    // availableCompanies를 먼저 추가
    availableCompanies.forEach((company) => {
      companyMap.set(company.companyName, company);
    });
    
    // licenses에 있는 회사도 추가 (availableCompanies에 없을 수 있음)
    licenses.forEach((license) => {
      if (!companyMap.has(license.companyName)) {
        companyMap.set(license.companyName, {
          orgCode: license.orgCode,
          companyName: license.companyName,
          dlpUseYn: 'Y',
          nacUseYn: 'N',
          aptUseYn: 'N',
        });
      }
    });
    
    return Array.from(companyMap.values()).sort((a, b) => 
      a.companyName.localeCompare(b.companyName)
    );
  }, [licenses, availableCompanies]);

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>DLP 라이센스구매수량 관리</h2>

      {message && (
        <div className={`message ${message.type}`} style={{ maxWidth: '50%', marginBottom: '1rem' }}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading">로딩 중...</div>
      ) : (
        <div style={{ maxWidth: '50%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={isSaving || isLoading}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>회사</th>
                <th>구매수량</th>
              </tr>
            </thead>
            <tbody>
              {allCompanies.length === 0 ? (
                <tr>
                  <td colSpan={2} className="no-data">
                    등록된 회사가 없습니다.
                  </td>
                </tr>
              ) : (
                <>
                  {allCompanies.map((company) => {
                    const license = licenses.find((l) => l.companyName === company.companyName);
                    const currentAmount = editedLicenses[company.companyName] || 
                      (license ? license.amount.toString() : '');
                    
                    const isIrongrey = company.companyName === 'IRONGREY';
                    
                    return (
                      <React.Fragment key={company.companyName}>
                        <tr>
                          <td>{company.companyName}</td>
                          <td>
                            <input
                              type="text"
                              value={currentAmount}
                              onChange={(e) => handleAmountChange(company.companyName, e.target.value)}
                              className="license-amount-input"
                              placeholder="0"
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                textAlign: 'right',
                              }}
                            />
                          </td>
                        </tr>
                        {/* IRONGREY 아래에 HPP 행 추가 */}
                        {isIrongrey && (
                          <tr>
                            <td>HPP</td>
                            <td>
                              <input
                                type="text"
                                value={editedLicenses['HPP'] || '4'}
                                onChange={(e) => handleAmountChange('HPP', e.target.value)}
                                className="license-amount-input"
                                placeholder="4"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* 총합 행 */}
                  <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    <td>총합</td>
                    <td>
                      {(
                        allCompanies.reduce((sum, company) => {
                          const license = licenses.find((l) => l.companyName === company.companyName);
                          const currentAmount = editedLicenses[company.companyName] || 
                            (license ? license.amount.toString() : '0');
                          const amountNum = parseInt(currentAmount.replace(/,/g, ''), 10) || 0;
                          return sum + amountNum;
                        }, 0) + 
                        (parseInt((editedLicenses['HPP'] || '4').replace(/,/g, ''), 10) || 4)
                      ).toLocaleString()}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* 엑셀 업로드 섹션 */}
      <div className="excel-upload-section" style={{ marginTop: '3rem' }}>
        <h3>엑셀 업로드</h3>
        
        {/* 업로드 상태 테이블 */}
        <div style={{ marginBottom: '2rem' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>주제</th>
                <th>업로드유무</th>
                <th>업로드날짜</th>
                <th>작성자</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>웹사이트 접속 현황내 이슈 사이트</td>
                <td>{uploadStatus?.issueSite?.uploaded ? '✓' : '✗'}</td>
                <td>
                  {uploadStatus?.issueSite?.uploadDate
                    ? new Date(uploadStatus.issueSite.uploadDate).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </td>
                <td>{uploadStatus?.issueSite?.register || '-'}</td>
              </tr>
              <tr>
                <td>전사 공통 유해사이트 차단 내역</td>
                <td>{uploadStatus?.commonBlockedSite?.uploaded ? '✓' : '✗'}</td>
                <td>
                  {uploadStatus?.commonBlockedSite?.uploadDate
                    ? new Date(uploadStatus.commonBlockedSite.uploadDate).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </td>
                <td>{uploadStatus?.commonBlockedSite?.register || '-'}</td>
              </tr>
              <tr>
                <td>라이선스 현황</td>
                <td>{uploadStatus?.licenseStat?.uploaded ? '✓' : '✗'}</td>
                <td>
                  {uploadStatus?.licenseStat?.uploadDate
                    ? new Date(uploadStatus.licenseStat.uploadDate).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </td>
                <td>{uploadStatus?.licenseStat?.register || '-'}</td>
              </tr>
              <tr>
                <td>퇴사자 라이선스 현황</td>
                <td>{uploadStatus?.exEmployee?.uploaded ? '✓' : '✗'}</td>
                <td>
                  {uploadStatus?.exEmployee?.uploadDate
                    ? new Date(uploadStatus.exEmployee.uploadDate).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </td>
                <td>{uploadStatus?.exEmployee?.register || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 파일 선택 및 업로드 버튼 */}
        <div style={{ marginBottom: '2rem' }}>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setSelectedFile(file);
                setUploadResult(null);
              }
            }}
            style={{ marginBottom: '1rem' }}
            id="excel-file-input-license"
          />
          {selectedFile && (
            <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              선택된 파일: {selectedFile.name}
            </div>
          )}
          <button
            className="btn-primary"
            onClick={async () => {
              if (!selectedFile) {
                setMessage({ type: 'error', text: '파일을 선택해주세요.' });
                return;
              }
              if (user?.role === 'reviewer') {
                alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
                return;
              }

              setIsUploading(true);
              setUploadResult(null);
              try {
                // 엑셀 파일에서 period와 company_name을 읽어오므로 빈 문자열 전달
                const result = await dlpService.uploadExcel(selectedFile, '', '');
                if (result.success) {
                  setUploadResult({ type: 'success', message: result.message });
                  setSelectedFile(null);
                  // 파일 input 초기화
                  const fileInput = document.getElementById('excel-file-input-license') as HTMLInputElement;
                  if (fileInput) fileInput.value = '';
                  // 업로드 상태 다시 로드
                  await loadUploadStatus();
                } else {
                  setUploadResult({
                    type: 'error',
                    message: result.message,
                    errors: result.errors,
                  });
                }
              } catch (error: any) {
                setUploadResult({
                  type: 'error',
                  message: error.response?.data?.message || '엑셀 업로드에 실패했습니다.',
                  errors: error.response?.data?.errors,
                });
              } finally {
                setIsUploading(false);
              }
            }}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? '업로드 중...' : selectedFile ? '실행' : '업로드'}
          </button>
        </div>

        {/* 실행 결과 메시지 영역 (실행 후에만 표시) */}
        {uploadResult && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              borderRadius: '4px',
              backgroundColor: uploadResult.type === 'success' ? '#d4edda' : '#f8d7da',
              color: uploadResult.type === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${uploadResult.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {uploadResult.type === 'success' ? '✓ 성공' : '✗ 오류'}
            </div>
            <div>{uploadResult.message}</div>
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ fontWeight: 'bold' }}>오류 상세:</div>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  {uploadResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// 라이센스 추가 모달
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LicenseAddModal: React.FC<{
  companies: CompanyInfo[];
  onClose: () => void;
  onSave: () => void;
}> = ({ companies, onClose, onSave }) => {
  const { user } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSave = async () => {
    if (!selectedCompany) {
      setError('회사를 선택해주세요.');
      return;
    }

    const amountNum = parseInt(amount.replace(/,/g, ''), 10);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('구매수량을 올바르게 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const company = companies.find((c) => c.companyName === selectedCompany);
      if (!company) {
        throw new Error('회사를 찾을 수 없습니다.');
      }

      await dlpService.createLicense({
        orgCode: company.orgCode,
        companyName: selectedCompany,
        amount: amountNum,
        updateDate: new Date(),
        updateUser: user?.email || 'system',
      });

      onSave();
    } catch (error: any) {
      setError(error.response?.data?.message || '라이센스 추가에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    setAmount(value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>라이센스 추가</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>회사:</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="form-input"
            >
              <option value="">선택하세요</option>
              {companies.map((company) => (
                <option key={company.orgCode} value={company.companyName}>
                  {company.companyName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>구매수량:</label>
            <input
              type="text"
              value={amount}
              onChange={handleAmountChange}
              placeholder="예: 1,000"
              className="form-input"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            취소
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

// DLP 예외처리 관리 섹션
const DlpExceptionManagementSection: React.FC = () => {
  const [requests, setRequests] = useState<DlpExceptionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState<DlpExceptionRequest | null>(null);
  const [exceptionGroups, setExceptionGroups] = useState<string[]>([]);
  const [exceptionDetails, setExceptionDetails] = useState<any[]>([]);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [filters, setFilters] = useState({
    exceptGroup: '',
    reqCompany: '',
    search: '',
  });

  useEffect(() => {
    loadData();
    loadExceptionGroups();
    loadCompanies();
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (filters.exceptGroup) {
      loadExceptionDetails(filters.exceptGroup);
    } else {
      setExceptionDetails([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.exceptGroup]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await dlpService.getExceptionRequests(filters);
      setRequests(data);
    } catch (error) {
      console.error('Failed to load exception requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadExceptionGroups = async () => {
    try {
      const groups = await dlpService.getExceptionGroups();
      setExceptionGroups(groups);
    } catch (error) {
      console.error('Failed to load exception groups:', error);
    }
  };

  const loadExceptionDetails = async (group: string) => {
    try {
      const details = await dlpService.getExceptionDetails(group);
      setExceptionDetails(details);
    } catch (error) {
      console.error('Failed to load exception details:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await companyService.getByPageType('DLP');
      setCompanies(data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const handleCreate = () => {
    setEditingRequest(null);
    setShowModal(true);
  };

  const handleEdit = (request: DlpExceptionRequest) => {
    setEditingRequest(request);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말로 이 예외처리 요청을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await dlpService.deleteExceptionRequest(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete exception request:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  // 엑셀 업로드 관련 상태
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);
  const [uploadExcelResult, setUploadExcelResult] = useState<{ type: 'success' | 'error'; message: string; errors?: string[] } | null>(null);
  const { user } = useAuth();

  const handleExcelUpload = () => {
    setShowExcelUploadModal(true);
    setSelectedExcelFile(null);
    setUploadExcelResult(null);
  };

  const handleExcelFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedExcelFile(file);
      setUploadExcelResult(null);
    }
  };

  const handleExecuteExcelUpload = async () => {
    if (!selectedExcelFile) {
      setUploadExcelResult({ type: 'error', message: '파일을 선택해주세요.' });
      return;
    }

    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    setIsUploadingExcel(true);
    setUploadExcelResult(null);

    try {
      const result = await dlpService.uploadExceptionRequestsExcel(selectedExcelFile);
      if (result.success) {
        setUploadExcelResult({ type: 'success', message: result.message });
        setSelectedExcelFile(null);
        // 파일 input 초기화
        const fileInput = document.getElementById('excel-file-input-exception') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // 데이터 다시 로드
        await loadData();
        // 2초 후 모달 닫기
        setTimeout(() => {
          setShowExcelUploadModal(false);
          setUploadExcelResult(null);
        }, 2000);
      } else {
        setUploadExcelResult({
          type: 'error',
          message: result.message,
          errors: result.errors,
        });
      }
    } catch (error: any) {
      setUploadExcelResult({
        type: 'error',
        message: error.response?.data?.message || '엑셀 업로드에 실패했습니다.',
      });
    } finally {
      setIsUploadingExcel(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>DLP 예외처리 관리</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={handleExcelUpload}>
            📤 엑셀 업로드
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            + 예외처리 추가
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          value={filters.exceptGroup}
          onChange={(e) => setFilters({ ...filters, exceptGroup: e.target.value })}
          style={{ padding: '0.5rem', minWidth: '150px' }}
        >
          <option value="">예외 항목 (전체)</option>
          {exceptionGroups.map((group) => (
            <option key={group} value={group}>
              {group}
            </option>
          ))}
        </select>
        <select
          value={filters.reqCompany}
          onChange={(e) => setFilters({ ...filters, reqCompany: e.target.value })}
          style={{ padding: '0.5rem', minWidth: '150px' }}
        >
          <option value="">신청자 계열사 (전체)</option>
          {companies.map((company) => (
            <option key={company.orgCode} value={company.companyName}>
              {company.companyName}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="검색 (이름, 부서, 사유, 대상자 계열사)"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ padding: '0.5rem', flex: 1, minWidth: '200px' }}
        />
      </div>

      {/* 목록 */}
      {isLoading ? (
        <div className="loading">로딩 중...</div>
      ) : requests.length === 0 ? (
        <div className="no-data">데이터가 없습니다.</div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>예외 항목</th>
                <th>예외 상세</th>
                <th>신청자 계열사</th>
                <th>신청자 부서</th>
                <th>신청자 이름</th>
                <th>대상자 계열사</th>
                <th>적용 여부</th>
                <th>예외 적용일</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>{request.id}</td>
                  <td>{request.exceptGroup}</td>
                  <td>{request.exceptDetailEnum?.itemName || request.exceptDetail}</td>
                  <td>{request.reqCompany}</td>
                  <td>{request.reqDepartment}</td>
                  <td>{request.reqName}</td>
                  <td>{request.targetCompany}</td>
                  <td>{request.applyYn || '-'}</td>
                  <td>{request.applyDate ? new Date(request.applyDate).toLocaleDateString() : '-'}</td>
                  <td>
                    <button
                      className="btn-secondary"
                      style={{ marginRight: '0.5rem' }}
                      onClick={() => handleEdit(request)}
                    >
                      수정
                    </button>
                    <button className="btn-danger" onClick={() => handleDelete(request.id!)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 모달 */}
      {showModal && (
        <ExceptionRequestModal
          request={editingRequest}
          exceptionGroups={exceptionGroups}
          exceptionDetails={exceptionDetails}
          companies={companies}
          onClose={() => {
            setShowModal(false);
            setEditingRequest(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingRequest(null);
            loadData();
          }}
        />
      )}

      {/* 엑셀 업로드 모달 */}
      {showExcelUploadModal && (
        <div className="modal-overlay" onClick={() => setShowExcelUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>예외처리 엑셀 업로드</h3>
              <button className="modal-close" onClick={() => setShowExcelUploadModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                  엑셀 파일 형식:
                </p>
                <ul style={{ marginLeft: '1.5rem', fontSize: '0.9rem', color: '#666' }}>
                  <li>시트명: <strong>DlpExceptionRequest</strong></li>
                  <li>첫 번째 행: 헤더 (필드명)</li>
                  <li>필수 필드: request_no</li>
                </ul>
                <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#999' }}>
                  필드: request_no, except_group, except_detail, req_company, req_department, req_name, target_company, permitted_ip, permitted_pc, reason, period, approval, actor, apply_yn, apply_date_display, apply_date, apply_method, remark
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileSelect}
                  id="excel-file-input-exception"
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>

              {selectedExcelFile && (
                <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  선택된 파일: {selectedExcelFile.name}
                </div>
              )}

              {uploadExcelResult && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: uploadExcelResult.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: uploadExcelResult.type === 'success' ? '#155724' : '#721c24',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {uploadExcelResult.type === 'success' ? '✓ 성공' : '✗ 오류'}
                  </div>
                  <div>{uploadExcelResult.message}</div>
                  {uploadExcelResult.errors && uploadExcelResult.errors.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>상세 오류:</div>
                      <ul style={{ marginLeft: '1.5rem', fontSize: '0.9rem' }}>
                        {uploadExcelResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowExcelUploadModal(false);
                    setSelectedExcelFile(null);
                    setUploadExcelResult(null);
                    const fileInput = document.getElementById('excel-file-input-exception') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  }}
                  disabled={isUploadingExcel}
                >
                  취소
                </button>
                <button
                  className="btn-primary"
                  onClick={handleExecuteExcelUpload}
                  disabled={!selectedExcelFile || isUploadingExcel || user?.role === 'reviewer'}
                >
                  {isUploadingExcel ? '업로드 중...' : '실행'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 예외처리 요청 모달
const ExceptionRequestModal: React.FC<{
  request: DlpExceptionRequest | null;
  exceptionGroups: string[];
  exceptionDetails: any[];
  companies: CompanyInfo[];
  onClose: () => void;
  onSave: () => void;
}> = ({ request, exceptionGroups, exceptionDetails, companies, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<DlpExceptionRequest>>({
    exceptGroup: request?.exceptGroup || '',
    exceptDetail: request?.exceptDetail || '',
    reqCompany: request?.reqCompany || '',
    reqDepartment: request?.reqDepartment || '',
    reqName: request?.reqName || '',
    targetCompany: request?.targetCompany || '',
    permittedIp: request?.permittedIp || '',
    permittedPc: request?.permittedPc || '',
    reason: request?.reason || '',
    period: request?.period || '',
    approval: request?.approval || '',
    actor: request?.actor || '',
    applyYn: request?.applyYn || '',
    applyDate: request?.applyDate || '',
    applyMethod: request?.applyMethod || '',
    remark: request?.remark || '',
  });
  const [selectedGroup, setSelectedGroup] = useState(request?.exceptGroup || '');
  const [availableDetails, setAvailableDetails] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (selectedGroup) {
      loadDetails(selectedGroup);
    } else {
      setAvailableDetails([]);
    }
  }, [selectedGroup]);

  const loadDetails = async (group: string) => {
    try {
      const details = await dlpService.getExceptionDetails(group);
      setAvailableDetails(details);
    } catch (error) {
      console.error('Failed to load exception details:', error);
    }
  };

  const handleGroupChange = (group: string) => {
    setSelectedGroup(group);
    setFormData({ ...formData, exceptGroup: group, exceptDetail: '' });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (request?.id) {
        await dlpService.updateExceptionRequest(request.id, formData);
      } else {
        await dlpService.createExceptionRequest(formData);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save exception request:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>{request ? '예외처리 수정' : '예외처리 추가'}</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group">
            <label>예외 항목 *</label>
            <select
              value={selectedGroup}
              onChange={(e) => handleGroupChange(e.target.value)}
              className="form-input"
              required
            >
              <option value="">선택하세요</option>
              {exceptionGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>예외 상세 *</label>
            <select
              value={formData.exceptDetail}
              onChange={(e) => setFormData({ ...formData, exceptDetail: e.target.value })}
              className="form-input"
              required
              disabled={!selectedGroup}
            >
              <option value="">선택하세요</option>
              {availableDetails.map((detail) => (
                <option key={detail.itemCode} value={detail.itemName}>
                  {detail.itemName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>신청자 계열사 *</label>
            <select
              value={formData.reqCompany}
              onChange={(e) => setFormData({ ...formData, reqCompany: e.target.value })}
              className="form-input"
              required
            >
              <option value="">선택하세요</option>
              {companies.map((company) => (
                <option key={company.orgCode} value={company.companyName}>
                  {company.companyName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>신청자 부서</label>
            <input
              type="text"
              value={formData.reqDepartment}
              onChange={(e) => setFormData({ ...formData, reqDepartment: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>신청자 이름</label>
            <input
              type="text"
              value={formData.reqName}
              onChange={(e) => setFormData({ ...formData, reqName: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>대상자 계열사</label>
            <input
              type="text"
              value={formData.targetCompany}
              onChange={(e) => setFormData({ ...formData, targetCompany: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>허용 PC IP 정보</label>
            <input
              type="text"
              value={formData.permittedIp}
              onChange={(e) => setFormData({ ...formData, permittedIp: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>허용PC 사용자/계정</label>
            <input
              type="text"
              value={formData.permittedPc}
              onChange={(e) => setFormData({ ...formData, permittedPc: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>사유</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="form-input"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>기간</label>
            <input
              type="date"
              value={formData.period}
              onChange={(e) => setFormData({ ...formData, period: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>승인자</label>
            <input
              type="text"
              value={formData.approval}
              onChange={(e) => setFormData({ ...formData, approval: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>처리자</label>
            <input
              type="text"
              value={formData.actor}
              onChange={(e) => setFormData({ ...formData, actor: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>적용 여부</label>
            <select
              value={formData.applyYn}
              onChange={(e) => setFormData({ ...formData, applyYn: e.target.value })}
              className="form-input"
            >
              <option value="">선택하세요</option>
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </div>
          <div className="form-group">
            <label>예외 적용일</label>
            <input
              type="date"
              value={formData.applyDate}
              onChange={(e) => setFormData({ ...formData, applyDate: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>처리방법</label>
            <input
              type="text"
              value={formData.applyMethod}
              onChange={(e) => setFormData({ ...formData, applyMethod: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>비고</label>
            <textarea
              value={formData.remark}
              onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
              className="form-input"
              rows={3}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            취소
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

// NAC 운영보고서 관리 섹션
const NacReportManagementSection: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [summaryContent, setSummaryContent] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const { user } = useAuth();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedCompany]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const periods = await nacService.getAvailablePeriods();
      setAvailablePeriods(periods);
      if (periods.length > 0) {
        setSelectedPeriod(periods[0]);
      }

      const companies = await companyService.getByPageType('NAC');
      setAvailableCompanies(companies);
      if (companies.length === 1) {
        setSelectedCompany(companies[0].companyName);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportData = async () => {
    if (!selectedPeriod || !selectedCompany) return;

    setIsLoading(true);
    try {
      // 요약 내용 로드
      const summary = await nacService.getSummary(selectedPeriod, selectedCompany);
      setSummaryContent(summary?.summaryContent || '');
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedPeriod || !selectedCompany) {
      setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
      return;
    }

    // reviewer인 경우 저장하지 않고 팝업 표시
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    if (!window.confirm('이 보고서를 발행하시겠습니까?')) {
      return;
    }

    setIsSaving(true);
    try {
      // 요약 저장
      await nacService.saveSummary(selectedPeriod, selectedCompany, summaryContent);

      // 퍼블리시
      await reportsService.publish(selectedPeriod, selectedCompany, 'NAC');
      setMessage({ type: 'success', text: '발행되었습니다.' });
      // 데이터 다시 로드
      loadReportData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '발행에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2>NAC 운영보고서 관리</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="report-management-controls">
        <div className="control-group">
          <label>기간:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availablePeriods.map((period) => (
              <option key={period} value={period}>
                {period.slice(0, 4)}년 {period.slice(4)}월
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>회사:</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availableCompanies.map((company) => (
              <option key={company.orgCode} value={company.companyName}>
                {company.companyName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedPeriod && selectedCompany && (
        <>
          <div className="summary-section" style={{ marginBottom: '3rem' }}>
            <h3>요약 작성</h3>
            <textarea
              className="summary-editor"
              value={summaryContent}
              onChange={(e) => setSummaryContent(e.target.value)}
              placeholder="Markdown 형식으로 요약을 작성하세요..."
              rows={10}
            />
            <button
              className="btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={async () => {
                if (!selectedPeriod || !selectedCompany) {
                  setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
                  return;
                }
                if (user?.role === 'reviewer') {
                  alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
                  return;
                }
                setIsSaving(true);
                try {
                  await nacService.saveSummary(selectedPeriod, selectedCompany, summaryContent);
                  setMessage({ type: 'success', text: '요약이 저장되었습니다.' });
                  loadReportData();
                } catch (error: any) {
                  setMessage({
                    type: 'error',
                    text: error.response?.data?.message || '저장에 실패했습니다.',
                  });
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>

          {/* PUBLISH 버튼 */}
          <div style={{ marginTop: '3rem', display: 'flex', justifyContent: 'flex-end', padding: '2rem 0', borderTop: '2px solid #ddd' }}>
            <button
              className="btn-primary"
              style={{ 
                padding: '0.75rem 2rem', 
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: '#28a745',
                borderColor: '#28a745',
              }}
              onClick={handlePublish}
              disabled={isSaving}
            >
              {isSaving ? '발행 중...' : 'PUBLISH'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// NAC 라이센스 관리 섹션
const NacLicenseManagementSection: React.FC = () => {
  return (
    <div>
      <h2>NAC 라이센스구매수량 관리</h2>
      <p>구현 예정</p>
    </div>
  );
};

// APT 스팸 신고 데이터 섹션
const AptSpamReportSection: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.CSV')) {
        setError('CSV 파일만 업로드 가능합니다.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('파일을 선택해주세요.');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const result = await resultsService.importSpamReport(selectedFile);
      setUploadResult(result);
      setSelectedFile(null);
      // 파일 input 초기화
      const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err: any) {
      console.error('업로드 실패:', err);
      setError(err.response?.data?.message || err.message || 'CSV 파일 업로드에 실패했습니다.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <h2>APT 스팸 신고 데이터 업로드</h2>
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          CSV 파일을 업로드하여 스팸 신고 데이터의 신고시각(report_time)과 신고분류(report_category)를 업데이트합니다.
        </p>
        <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
          CSV 파일 형식: 이메일주소, 구분(회사), 성명, 신고 시각, 신고 분류 컬럼이 포함되어야 합니다.
        </p>
      </div>

      <div style={{ 
        background: 'white', 
        padding: '2rem', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="csv-file-input" style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            fontWeight: 500,
            fontSize: '1rem'
          }}>
            CSV 파일 선택:
          </label>
          <input
            id="csv-file-input"
            type="file"
            accept=".csv,.CSV"
            onChange={handleFileChange}
            disabled={isUploading}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
          {selectedFile && (
            <p style={{ marginTop: '0.5rem', color: '#28a745', fontSize: '0.9rem' }}>
              선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </p>
          )}
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedFile && !isUploading ? '#007bff' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFile && !isUploading ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          {isUploading ? '업로드 중...' : 'CSV 파일 업로드'}
        </button>
      </div>

      {uploadResult && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}>
          <h3 style={{ marginBottom: '1rem' }}>업로드 결과</h3>
          <div style={{ marginBottom: '1rem' }}>
            <p><strong>매칭 완료:</strong> {uploadResult.summary?.matched || 0}건</p>
            <p><strong>불일치:</strong> {uploadResult.summary?.mismatched || 0}건</p>
            <p><strong>CSV에서 찾을 수 없음:</strong> {uploadResult.summary?.notFoundInCsv || 0}건</p>
            <p><strong>DB에서 찾을 수 없음 (CSV만 존재):</strong> {uploadResult.summary?.csvOnly || 0}건</p>
          </div>

          {uploadResult.mismatches && uploadResult.mismatches.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#dc3545' }}>불일치 데이터 (최대 50개):</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>이메일</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>DB 회사</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>DB 이름</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>CSV 회사</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>CSV 이름</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.mismatches.map((mismatch: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{mismatch.email}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{mismatch.dbCompany}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{mismatch.dbName}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{mismatch.csvCompany}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{mismatch.csvName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uploadResult.notFound && uploadResult.notFound.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#ffc107' }}>CSV에서 찾을 수 없는 DB 데이터 (최대 50개):</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>이메일</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>회사</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>이름</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.notFound.map((item: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{item.email}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{item.company}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{item.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uploadResult.csvOnly && uploadResult.csvOnly.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#17a2b8' }}>DB에서 찾을 수 없는 CSV 데이터 (최대 50개):</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>이메일</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>회사</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>이름</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.csvOnly.map((item: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{item.email}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{item.company}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{item.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage;

